import { get } from 'lodash';
import type { CreateControllerFn, I$W } from '@wix/yoshi-flow-editor';
import { createSlotVeloAPIFactory } from '@wix/widget-plugins-ooi/velo';
import { createPerformanceTracker } from '@wix/communities-blog-client-common';
import { type WithReduxStoreOwnProps } from '../../common/components/runtime-context/with-redux-store';
import {
  createControllerId,
  createLogger,
  doRouting,
  getSectionPathname,
  handleLocationChange,
} from '../../common/controller/helpers';
import { initLazyActions } from '../../common/controller/lazy-actions';
import { getPlatformApi } from '../../common/controller/platform-api';
import { setRouterMatch } from '../../common/router';
import { getCurrentSiteLanguage } from '../../common/services/get-current-site-language';
import getEnvironment from '../../common/services/get-environment';
import { isRtlLanguage } from '../../common/services/is-rtl-language';
import listenToInstanceChange from '../../common/services/listen-to-instance-change';
import { toMonitoredViewer } from '../../common/services/monitoring';
import { simulateControllerError } from '../../common/services/simulate-error';
import { subscribeToChange } from '../../common/services/state-optimizer';
import { getInitialStateVersions } from '../../common/services/state-optimizer/change-detector';
import { setAppSettings } from '../../common/store/app-settings/app-settings-actions';
import type { AppStore } from '../../common/types';
import { buildPostPageSectionUrl } from '../services/get-section-url';
import { createPostPageApi } from './create-post-page-api';
import { createReduxStore } from './create-redux-store';
import { createRouter } from './create-router';
import {
  initializeActions,
  initializePromisifiedActions,
  initializeStoreBaseData,
  refreshDataOnLogin,
} from './init-actions';

const isProduction = process.env.NODE_ENV === 'production';

type RootProps = WithReduxStoreOwnProps & {
  isRTL: boolean;
  fitToContentHeight: boolean;
};

export const createPostPageController: CreateControllerFn = ({ controllerConfig, flowAPI }) =>
  toMonitoredViewer(flowAPI.panoramaClient, 'post-page.create-controller', async () => {
    const { appParams, compId, config, setProps, platformAPIs } = controllerConfig;

    const platformApi = await getPlatformApi(
      platformAPIs.platformApiProvider,
      false,
      flowAPI.panoramaClient,
    );
    const { isEditor, isPreview, isSSR, isDebug } = getEnvironment(platformApi);

    const language = getCurrentSiteLanguage(platformApi, flowAPI);

    const bundleName = 'post-page' as const;

    const log = createLogger(isDebug, isProduction);
    const perf = createPerformanceTracker(bundleName, { isDebug, isSSR });

    log('createPostPageController', {
      appParams,
      platformApi,
      isSSR,
      language,
    });

    let store: AppStore;

    const pageReady = () =>
      toMonitoredViewer(flowAPI.panoramaClient, 'post-page.page-ready', async () => {
        const pageReadyMarker = perf.trackStart(`${new Date().toISOString().slice(11)} pageReady`);
        log('createPostPageController.pageReady -> start');

        const sectionUrl = await buildPostPageSectionUrl({
          platformApi,
          instanceId: appParams.instanceId,
        });

        simulateControllerError(platformApi, 'post-page.pageReady');

        initLazyActions({ isSSR });

        let marker = perf.trackStart('createReduxStore', pageReadyMarker);
        store = createReduxStore({
          appParams,
          platformApi,
          compId,
          getRouter: () => router,
          platformAPIs,
          isSSR,
          isEditor,
          isPreview,
          language,
          bundleName,
          flowAPI,
        });
        perf.trackEnd(marker);

        marker = perf.trackStart('initializeActions', pageReadyMarker);
        const actions = initializeActions({
          controllerConfig,
          store,
          flowAPI,
          platformApi,
        });
        const actionsPromisified = initializePromisifiedActions({
          store,
          controllerConfig,
          flowAPI,
        });
        perf.trackEnd(marker);

        marker = perf.trackStart('createRouter', pageReadyMarker);
        const slotAPIFactory = createSlotVeloAPIFactory(controllerConfig);

        const router = createRouter({
          store,
          platformApi,
          flowAPI,
          slotAPIFactory,
          controllerConfig,
        });
        perf.trackEnd(marker);

        const matchedRoute = await router.matchPath({
          pathname: getSectionPathname({ platformApi, sectionUrl }),
        });
        const route = get(matchedRoute, 'route');

        await perf.trackPromise(
          'initializeStoreBaseData',
          () =>
            initializeStoreBaseData({
              platformApi,
              store,
              language,
              platformAPIs,
              config,
              bundleName,
              preFetch: () => router.preFetch(getSectionPathname({ platformApi, sectionUrl })),
              appParams,
              route,
            }),
          pageReadyMarker,
        );
        listenToInstanceChange(platformApi, appParams, store);

        handleLocationChange({
          platformApi,
          store,
          log,
          bundleName,
          router,
          sectionUrl,
        });
        await perf.trackPromise(
          'doRouting',
          () =>
            doRouting({
              store,
              router,
              pathname: getSectionPathname({
                platformApi,
                sectionUrl,
              }),
              isInitialLoad: true,
            }),
          pageReadyMarker,
        );

        log('createPostPageController.pageReady -> done');
        const state = store.getState();

        // after initial routing is done, we subscribe to get routing change as fast as route changes, without waiting for handler to resolve
        router.onMatch((match: any) => store.dispatch(setRouterMatch(match)));

        const stateVersions = getInitialStateVersions(state);
        const controllerId = createControllerId();

        setProps({
          state,
          stateVersions,
          actions,
          actionsPromisified,
          isSSR,
          isRTL: isRtlLanguage(language),
          controllerId,
          fitToContentHeight: true,
        } satisfies RootProps);

        refreshDataOnLogin({ platformApi, store, router });
        subscribeToChange(store, stateVersions, setProps, controllerId);
        perf.trackEnd(pageReadyMarker);
      });

    return {
      pageReady,
      exports: () => createPostPageApi(appParams.instance, platformApi, flowAPI),
      updateConfig: (_$w: I$W, { style: { styleParams } }) => {
        store && store.dispatch(setAppSettings({ style: styleParams }));
      },
    };
  });
